import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateLeadRequest, UpdateLeadRequest, ConvertLeadRequest, TransferRequest } from '@troia-v3/types';
import { leadsService } from '../services';

/**
 * Hook para criar novo lead
 *
 * @example
 * ```tsx
 * const createLead = useCreateLead();
 *
 * const handleCreate = async () => {
 *   await createLead.mutateAsync({ contactId: '...', ... });
 * };
 * ```
 */
export const useCreateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: CreateLeadRequest & { _handleErrorLocally?: boolean }) => {
      const { _handleErrorLocally, ...data } = variables;
      return leadsService.createLead(data, _handleErrorLocally ? { handleErrorLocally: true } : undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
};

/**
 * Hook para atualizar lead existente
 *
 * @example
 * ```tsx
 * const updateLead = useUpdateLead();
 *
 * const handleUpdate = async () => {
 *   await updateLead.mutateAsync({ id: 'lead-id', data: { priority: 'high' } });
 * };
 * ```
 */
export const useUpdateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLeadRequest }) =>
      leadsService.updateLead(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.id] });
    },
  });
};

/**
 * Hook para excluir lead
 *
 * @example
 * ```tsx
 * const deleteLead = useDeleteLead();
 *
 * const handleDelete = async (leadId: string) => {
 *   await deleteLead.mutateAsync(leadId);
 * };
 * ```
 */
export const useDeleteLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => leadsService.deleteLead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      // O delete cascateia soft-delete das atividades do lead no backend. Invalidar
      // as views que agregam atividades para não exibir órfãos em cache (agenda usa
      // 'calendar-events-range'; sino/widgets usam 'activities'/'my-activities').
      queryClient.invalidateQueries({ queryKey: ['calendar-events-range'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['my-activities'] });
    },
  });
};

/**
 * Hook para converter lead em cliente
 *
 * @example
 * ```tsx
 * const convertLead = useConvertLead();
 *
 * const handleConvert = async () => {
 *   await convertLead.mutateAsync({ id: 'lead-id', data: { customerId: '...' } });
 * };
 * ```
 */
export const useConvertLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConvertLeadRequest }) =>
      leadsService.convertLead(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.id] });
    },
  });
};

/**
 * Hook para transferir lead para outro usuário/time (ou desatribuir)
 *
 * Endpoint padronizado: `POST /leads/:id/transfer`
 * Body aceita XOR `toUserId | toTeamId` (null desatribui) + `reason?`/`notes?`.
 */
export const useTransferLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, body }: { leadId: string; body: TransferRequest }) =>
      leadsService.transfer(leadId, body),
    onSuccess: (lead, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      if (lead?.id) {
        queryClient.setQueryData(['lead', lead.id], lead);
      }
    },
  });
};

/**
 * Hook para mover lead para outra etapa
 */
export const useMoveLeadToStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, stepId }: { id: string; stepId: string }) =>
      leadsService.moveLeadToStep(id, stepId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.id] });
    },
  });
};

/**
 * Hook para marcar lead como ganho
 */
export const useMarkLeadAsWon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, wonValue }: { id: string; wonValue?: number }) =>
      leadsService.markAsWon(id, wonValue),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.id] });
    },
  });
};

/**
 * Hook para marcar lead como perdido
 */
export const useMarkLeadAsLost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, lostReason }: { id: string; lostReason?: string }) =>
      leadsService.markAsLost(id, lostReason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.id] });
    },
  });
};
